import { AttachmentService } from "../services/attachment.service";
import { OrganizationMicroService } from "../services/organization.micro.service";
import { S25WsMicroOrganization } from "../services/MicroServices/S25WsMicroOrganization";
import { S25WsMicroService } from "../services/MicroServices/S25WsMicroService";
import { Item } from "../pojo/Item";
import { FileStoreParams, FileStoreUtil } from "./filestore.util";

import ItemAttribute = S25WsMicroService.ItemAttribute;

export type OrgFileAttributeRefDetail = {
    refCount: number;
    /** Display names from expanded org detail when available. */
    attributeLabels: string[];
};

function sameOrgFilestoreFile(a: FileStoreParams, b: FileStoreParams): boolean {
    return a.objectType === b.objectType && Number(a.objectId) === Number(b.objectId) && a.fileName === b.fileName;
}

function attributeNameMap(content: S25WsMicroOrganization.Content): Map<number, string> {
    const map = new Map<number, string>();
    const expanded = content.expandedInfo?.[0]?.attributes;
    if (!expanded?.length) {
        return map;
    }
    for (const info of expanded) {
        map.set(info.attributeId, info.attributeName);
    }
    return map;
}

/**
 * Lists organization custom attribute values that reference the same filestore file
 * (same object_type, object_id, file_name), with human-readable attribute names when expand=all.
 */
export async function attributeRefDetailForOrgFile(
    orgId: number,
    target: FileStoreParams,
): Promise<OrgFileAttributeRefDetail> {
    const content = await OrganizationMicroService.getOrganizationIncludes([orgId], ["attributes"], true);
    const item = content.data.items[0];
    const attrs = item?.attributes;
    if (!attrs?.length) {
        return { refCount: 0, attributeLabels: [] };
    }
    const nameById = attributeNameMap(content);
    const labels: string[] = [];
    //filestoreCA's are stored as a partial url eg. '(e.g., "object_type=2&object_id=43&atrb_id=-101&file_name=Pickles.jpg")
    //here we extract extract the files in use by the given org and compare. It could be helpful for safe delete.
    for (const attr of attrs) {
        const v = attr.value;
        if (typeof v !== "string" || !FileStoreUtil.isFileStoreUrl(v)) {
            continue;
        }
        const p = FileStoreUtil.parseFileStoreUrl(v);
        if (!p || !sameOrgFilestoreFile(p, target)) {
            continue;
        }
        labels.push(labelForAttribute(attr, nameById));
    }
    return { refCount: labels.length, attributeLabels: labels };
}

function labelForAttribute(attr: ItemAttribute, nameById: Map<number, string>): string {
    return nameById.get(attr.attributeId)?.trim() || `Attribute ID ${attr.attributeId}`;
}

/**
 * Counts organization custom attribute values that reference the same filestore file
 * (same object_type, object_id, file_name).
 */
export async function countAttributeRefsToOrgFile(orgId: number, target: FileStoreParams): Promise<number> {
    const d = await attributeRefDetailForOrgFile(orgId, target);
    return d.refCount;
}

/**
 * After replacing or clearing a URL custom attribute value, delete the filestore file only when
 * no other attribute on the organization still references it.
 */
export async function maybeDeleteOldFileAfterOrgCaReplace(
    orgId: number,
    previousCaValue: string | undefined,
    fileKey?: string,
): Promise<void> {
    if (!previousCaValue?.trim() || !FileStoreUtil.isFileStoreUrl(previousCaValue)) {
        return;
    }
    const p = FileStoreUtil.parseFileStoreUrl(previousCaValue);
    if (!p || p.objectType !== Item.Ids.Organization || p.objectId !== orgId) {
        return;
    }
    const n = await countAttributeRefsToOrgFile(orgId, p);
    if (n > 1) {
        return;
    }
    await AttachmentService.delFile(AttachmentService.ObjectType.ORGANIZATION, orgId, p.fileName, fileKey);
}

/** Same as {@link countAttributeRefsToOrgFile}; kept for call sites that only need a number. */
export async function attributeRefCountForOrgFile(orgId: number, target: FileStoreParams): Promise<number> {
    return countAttributeRefsToOrgFile(orgId, target);
}
